/**
 * Privacy Policy Page
 *
 * Professional placeholder template - customize with your actual privacy policy
 */

import { Link } from 'react-router-dom';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link
            to="/"
            className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-4"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Home
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Privacy Policy
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg dark:prose-invert max-w-none">

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Our Commitment to Privacy
            </h2>
            <div className="bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-500 p-4 mb-4">
              <p className="text-primary-900 dark:text-primary-100 font-semibold">
                🔒 Privacy-First Design
              </p>
              <p className="text-primary-800 dark:text-primary-200 mt-2">
                myTotTot is built with zero-knowledge end-to-end encryption. This means we cannot read your pregnancy data, baby's data, event details, or notes - even if we wanted to.
              </p>
            </div>
            <p className="text-gray-700 dark:text-gray-300">
              This Privacy Policy explains how myTotTot ("we," "our," or "us") collects, uses, and protects your information when you use our baby tracking application and services (the "Service").
            </p>
          </section>

          {/* What We Collect */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              1. Information We Collect
            </h2>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
              Account Information
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              When you create an account, we collect:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li><strong>Email address</strong> - Used for account access and important notifications</li>
              <li><strong>Name</strong> - To personalize your experience</li>
              <li><strong>Password</strong> - Stored as a secure hash (we never see your actual password)</li>
              <li><strong>Encryption keys</strong> - Your RSA public/private key pair (private key is encrypted with your password)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
              Encrypted Data (We Cannot Read This)
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              The following data is encrypted on your device before reaching our servers:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li><strong>Profile data</strong> - Baby profiles (names, birth dates, gender, pediatrician info) and pregnancy profiles (due dates, pre-pregnancy weight, OB/GYN details)</li>
              <li><strong>Events</strong> - Pregnancy symptoms, kick counts, contractions, appointments, as well as baby feeding times, diaper changes, sleep patterns, medications, etc.</li>
              <li><strong>Notes</strong> - Any personal notes you add to events</li>
              <li><strong>Photos</strong> - Pregnancy and baby photos (encrypted before upload to cloud storage)</li>
            </ul>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mt-4">
              <p className="text-green-800 dark:text-green-200">
                ✅ <strong>Zero-Knowledge Architecture:</strong> All sensitive data is encrypted with AES-256-GCM on your device. Only you have the keys to decrypt it.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
              Usage Information
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              We collect non-sensitive usage data to improve the Service:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li>Login timestamps</li>
              <li>Device type and browser information</li>
              <li>IP addresses (for security purposes)</li>
              <li>Feature usage statistics (anonymous)</li>
            </ul>
          </section>

          {/* How We Use Data */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              2. How We Use Your Information
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              We use your information to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li><strong>Provide the Service</strong> - Store and sync your encrypted data across devices</li>
              <li><strong>Account management</strong> - Handle authentication, password resets, and security</li>
              <li><strong>Communication</strong> - Send important service updates and support responses</li>
              <li><strong>Security</strong> - Detect and prevent fraud, abuse, and unauthorized access</li>
              <li><strong>Improvements</strong> - Analyze anonymous usage patterns to enhance features</li>
              <li><strong>Compliance</strong> - Meet legal obligations and protect rights</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 mt-4">
              <strong>We will never:</strong>
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2 mt-2">
              <li>Sell your personal information to third parties</li>
              <li>Use your data for advertising purposes</li>
              <li>Share your encrypted data with anyone (we can't - it's encrypted!)</li>
              <li>Read your baby's information or event details</li>
            </ul>
          </section>

          {/* Data Storage */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              3. Data Storage and Security
            </h2>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
              Encryption Details
            </h3>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2 mb-4">
              <li><strong>Algorithm:</strong> AES-256-GCM (industry-standard encryption)</li>
              <li><strong>Key Management:</strong> Each baby profile has its own encryption key</li>
              <li><strong>Client-Side:</strong> All encryption happens on your device</li>
              <li><strong>Zero Access:</strong> Server only stores encrypted ciphertext</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
              Data Location
            </h3>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2 mb-4">
              <li><strong>Database:</strong> Hosted on secure cloud infrastructure</li>
              <li><strong>Photos:</strong> Stored in Cloudflare R2 (encrypted before upload)</li>
              <li><strong>Backups:</strong> Regular encrypted backups maintained</li>
              <li><strong>Transit:</strong> All data transmitted over HTTPS/TLS</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
              Security Measures
            </h3>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li>Password hashing with secure algorithms</li>
              <li>Two-factor authentication (2FA) support</li>
              <li>Session management and automatic timeout</li>
              <li>Audit logging of sensitive actions</li>
              <li>Regular security audits and updates</li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              4. Data Sharing
            </h2>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
              When You Share
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              You control who sees your baby's data:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li><strong>Invitations:</strong> You can invite other users to access a baby profile</li>
              <li><strong>Pediatrician Sharing:</strong> You can create temporary encrypted links for healthcare providers</li>
              <li><strong>Emergency Access:</strong> You designate trusted contacts who can request emergency access</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
              Service Providers
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              We use trusted third-party services:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li><strong>Cloud hosting</strong> - For database and application servers</li>
              <li><strong>Cloudflare R2</strong> - For encrypted photo storage</li>
              <li><strong>Email service</strong> - For transactional emails (password resets, notifications)</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 mt-4">
              These providers only have access to encrypted data and cannot decrypt it.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
              Legal Requirements
            </h3>
            <p className="text-gray-700 dark:text-gray-300">
              We may disclose information if required by law, but remember: your encrypted data cannot be decrypted without your keys.
            </p>
          </section>

          {/* Your Rights */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              5. Your Rights and Choices
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              You have the following rights:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-3">
              <li>
                <strong>Access:</strong> View all your account information through the settings page
              </li>
              <li>
                <strong>Export:</strong> Download all your data in JSON format (encrypted data is decrypted for export)
              </li>
              <li>
                <strong>Correction:</strong> Update your account information at any time
              </li>
              <li>
                <strong>Deletion:</strong> Delete your account and all associated data
              </li>
              <li>
                <strong>Portability:</strong> Take your data with you (via export feature)
              </li>
              <li>
                <strong>Revocation:</strong> Remove access from other users or emergency contacts
              </li>
            </ul>
          </section>

          {/* Data Retention */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              6. Data Retention
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              We retain your data as follows:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li><strong>Active accounts:</strong> Data retained while your account is active</li>
              <li><strong>Inactive accounts:</strong> After 12 months of inactivity, we send reminders</li>
              <li><strong>Deleted accounts:</strong> All data permanently deleted within 30 days</li>
              <li><strong>Audit logs:</strong> Security logs retained for 90 days</li>
              <li><strong>Backups:</strong> Deleted data removed from backups within 30 days</li>
            </ul>
          </section>

          {/* Children's Privacy */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              7. Children's Privacy
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              myTotTot is designed for parents and caregivers to track their babies' information:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li>Users must be 18+ or have parental consent</li>
              <li>The Service is not directed at children</li>
              <li>Baby data is controlled by parent/caregiver accounts</li>
              <li>All baby information is encrypted for maximum privacy</li>
            </ul>
          </section>

          {/* International Users */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              8. International Data Transfers
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              Our servers may be located in different countries. By using the Service, you consent to the transfer of your encrypted data to these locations. All data remains encrypted regardless of location.
            </p>
          </section>

          {/* Cookies */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              9. Cookies and Tracking
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              We use minimal tracking technologies:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li><strong>Essential cookies:</strong> Required for authentication and session management</li>
              <li><strong>Local storage:</strong> Used to cache encrypted data for offline access</li>
              <li><strong>No advertising cookies:</strong> We don't use cookies for ads or marketing</li>
              <li><strong>No third-party trackers:</strong> No Google Analytics, Facebook Pixel, etc.</li>
            </ul>
          </section>

          {/* Changes to Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              10. Changes to This Policy
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              We may update this Privacy Policy from time to time. We will notify you of material changes by email or through the Service. The "Last updated" date at the top will always reflect the current version.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              11. Contact Us
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              If you have questions about this Privacy Policy or our privacy practices:
            </p>
            <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                <strong>Email:</strong> <a href="mailto:support@mytottot.app" className="text-primary-600 hover:text-primary-700">support@mytottot.app</a>
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                <strong>Subject:</strong> Privacy Policy Inquiry
              </p>
            </div>
          </section>

          {/* Summary Box */}
          <section className="mb-8">
            <div className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-800/30 rounded-xl p-6 border border-primary-200 dark:border-primary-700">
              <h3 className="text-xl font-semibold text-primary-900 dark:text-primary-100 mb-4">
                🔐 Privacy Summary
              </h3>
              <ul className="space-y-2 text-primary-800 dark:text-primary-200">
                <li>✅ Zero-knowledge encryption - we can't read your data</li>
                <li>✅ No data selling or advertising</li>
                <li>✅ You control who sees your information</li>
                <li>✅ Export your data anytime</li>
                <li>✅ Delete your account permanently</li>
                <li>✅ Open source security (coming soon)</li>
              </ul>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              © {new Date().getFullYear()} myTotTot. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400">
                Terms of Service
              </Link>
              <a href="mailto:support@mytottot.app" className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400">
                Support
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
